import { githubCompleteInstallationRequestSchema, githubCompleteInstallationResultSchema, githubConnectResultSchema, githubDecodeStateRequestSchema, githubDecodeStateResponseSchema, githubDisconnectResultSchema, githubImportedUsersResultSchema, githubLinkCallbackRequestSchema, githubLinkCallbackResultSchema, githubLinkInitiateResultSchema, githubLinkStatusResponseSchema, githubManualConnectRequestSchema, githubManualConnectResultSchema, githubStatusResponseSchema, githubSyncResultSchema, githubSyncStatusResponseSchema, githubUnlinkResultSchema, } from '../contracts/github';
import { organizationIdSchema } from '../contracts/organizations';
import { parseSchema } from '../errors';
/**
 * Get GitHub integration status for an organization
 */
export async function getGithubStatus(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/github/status`);
    return parseSchema(githubStatusResponseSchema, response);
}
/**
 * Initiate GitHub connection flow for an organization
 * Returns the GitHub App installation URL when enabled, or { enabled: false } when disabled
 */
export async function connectGithub(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.post(`/organizations/${validatedId}/github/connect`);
    return parseSchema(githubConnectResultSchema, response);
}
/**
 * Disconnect GitHub integration from an organization
 * Returns success or { enabled: false } when feature is disabled
 */
export async function disconnectGithub(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.post(`/organizations/${validatedId}/github/disconnect`);
    return parseSchema(githubDisconnectResultSchema, response);
}
/**
 * Decode a signed state token from GitHub callback
 * Used to determine which organization to redirect to
 */
export async function decodeGithubState(client, data) {
    const validatedData = parseSchema(githubDecodeStateRequestSchema, data);
    const response = await client.post('/github/decode-state', validatedData);
    return parseSchema(githubDecodeStateResponseSchema, response);
}
/**
 * Complete GitHub App installation
 * Called after GitHub redirects back with installation_id
 * Returns success with githubOrgName, or { enabled: false } when feature is disabled
 */
export async function completeGithubInstallation(client, organizationId, data) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedData = parseSchema(githubCompleteInstallationRequestSchema, data);
    const response = await client.post(`/organizations/${validatedId}/github/complete-installation`, validatedData);
    return parseSchema(githubCompleteInstallationResultSchema, response);
}
/**
 * Manually connect a GitHub installation by providing an installation ID directly
 * Bypasses the callback flow - useful for dev/testing or recovery
 * Returns success with githubOrgName, or { enabled: false } when feature is disabled
 */
export async function connectGithubManual(client, organizationId, data) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedData = parseSchema(githubManualConnectRequestSchema, data);
    const response = await client.post(`/organizations/${validatedId}/github/connect-manual`, validatedData);
    return parseSchema(githubManualConnectResultSchema, response);
}
/**
 * Trigger a manual sync of GitHub organization members
 * Only one sync can run at a time per organization
 * Returns sync results or { enabled: false } when feature is disabled
 */
export async function syncGithub(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.post(`/organizations/${validatedId}/github/sync`);
    return parseSchema(githubSyncResultSchema, response);
}
/**
 * Get GitHub sync status for an organization
 * Includes last sync time, status, and user counts
 */
export async function getGithubSyncStatus(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/github/sync/status`);
    return parseSchema(githubSyncStatusResponseSchema, response);
}
/**
 * Get imported users from GitHub for an organization
 * Returns users that have been synced from GitHub but not yet linked to Kirin accounts
 */
export async function getGithubImportedUsers(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/github/imported-users`);
    return parseSchema(githubImportedUsersResultSchema, response);
}
// ============================================================================
// ACCOUNT LINKING
// ============================================================================
/**
 * Initiate GitHub account linking flow
 * Returns the OAuth authorization URL to redirect the user to
 */
export async function initiateGithubLink(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.post(`/organizations/${validatedId}/github/link/initiate`);
    return parseSchema(githubLinkInitiateResultSchema, response);
}
/**
 * Complete GitHub account linking after OAuth callback
 * Exchanges the authorization code and links the GitHub account
 */
export async function completeGithubLink(client, organizationId, data) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedData = parseSchema(githubLinkCallbackRequestSchema, data);
    const response = await client.post(`/organizations/${validatedId}/github/link/callback`, validatedData);
    return parseSchema(githubLinkCallbackResultSchema, response);
}
/**
 * Get the current user's GitHub link status
 * Returns whether the user has linked their GitHub account
 */
export async function getGithubLinkStatus(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/github/link/status`);
    return parseSchema(githubLinkStatusResponseSchema, response);
}
/**
 * Unlink the current user's GitHub account
 * Allows users to unlink their own account so they can link a different one
 */
export async function unlinkGithubLink(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.delete(`/organizations/${validatedId}/github/link`);
    return parseSchema(githubUnlinkResultSchema, response);
}
//# sourceMappingURL=github.js.map